<template>
  <div class="sas-page">
    <div class="sas-header">
      <h1 class="sas-title">服務管理</h1>
      <div class="sas-header-right">
        <span v-if="showObHint" class="sas-onboard-finger">👉 點這裡新增服務</span>
        <button
          class="sas-btn sas-btn--primary"
          :class="{ 'sas-btn--onboard-pulse': showObHint }"
          @click="openCreate"
        >＋ 新增服務</button>
      </div>
    </div>

    <!-- 手機版 onboarding 引導按鈕（左下角，點擊直接開啟新增） -->
    <button v-if="showObHint" class="sas-fab-ob" @click="openCreate">
      <span class="sas-fab-ob__icon">↑</span>
      <span class="sas-fab-ob__label">下一步 新增服務</span>
    </button>

    <!-- 手機版浮動新增按鈕 -->
    <button class="sas-fab" @click="openCreate" aria-label="新增服務">＋</button>

    <!-- 新手引導：新增服務後的下一步 -->
    <div v-if="showNextStep" class="sas-next-step">
      <span class="sas-next-step__ok">✅ 服務已新增！</span>
      <span class="sas-next-step__label">👉 點右側「＋ 新增時段」，為這個服務建立可預約的時段</span>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="sas-state">
      <span class="sas-spinner" />
      <p>載入服務項目…</p>
    </div>

    <!-- 載入失敗 -->
    <div v-else-if="loadError" class="sas-state sas-state--error">
      <p>{{ loadError }}</p>
      <button class="sas-btn sas-btn--secondary" @click="load">重新載入</button>
    </div>

    <!-- 空列表 -->
    <div v-else-if="services.length === 0" class="sas-empty">
      <p>尚未建立任何服務項目</p>
      <button class="sas-btn sas-btn--primary" @click="openCreate">新增第一筆服務</button>
    </div>

    <!-- 服務列表 -->
    <div v-else class="sas-list">
      <div
        v-for="svc in services"
        :key="svc.id"
        class="sas-card"
        :class="{ 'sas-card--inactive': svc.status === 'inactive' }"
      >
        <!-- 左側資訊 -->
        <div class="sas-card__info">
          <div class="sas-card__top">
            <span class="sas-card__name">{{ svc.name }}</span>
            <span
              class="sas-badge"
              :class="svc.status === 'active' ? 'sas-badge--active' : 'sas-badge--inactive'"
            >
              {{ svc.status === 'active' ? '上架中' : '已停用' }}
            </span>
          </div>
          <p v-if="svc.description" class="sas-card__desc">{{ svc.description }}</p>
          <div class="sas-card__items">
            <span
              v-for="it in (svc.service_items || [])"
              :key="it.id"
              class="sas-card__item-tag"
            >{{ it.name }}・{{ it.duration_minutes }}分・NT${{ Number(it.price).toLocaleString() }}{{ it.price_note ? ' ' + it.price_note : '' }}</span>
          </div>
        </div>

        <!-- 右側操作 -->
        <div class="sas-card__actions">
          <button
            class="sas-btn sas-btn--sm sas-btn--add-slot"
            :class="{ 'sas-btn--ob-pulse': showNextStep }"
            @click="openBatchForSvc(svc)"
          >
            ＋ 新增時段
          </button>
          <button
            class="sas-btn sas-btn--sm sas-btn--slots"
            @click="goToSlots(svc)"
          >
            🕐 時段管理
          </button>
          <button
            class="sas-btn sas-btn--sm sas-btn--secondary"
            @click="openEdit(svc)"
          >
            編輯
          </button>
          <button
            class="sas-btn sas-btn--sm"
            :class="svc.status === 'active' ? 'sas-btn--deactivate' : 'sas-btn--activate'"
            :disabled="toggleLoading[svc.id]"
            @click="toggleStatus(svc)"
          >
            {{ svc.status === 'active' ? '停用' : '啟用' }}
          </button>
          <button
            class="sas-btn sas-btn--sm sas-btn--danger"
            :disabled="deleteLoading[svc.id]"
            @click="deleteService(svc)"
          >
            刪除
          </button>
        </div>

        <!-- 操作錯誤（逐筆顯示）-->
        <p v-if="cardErrors[svc.id]" class="sas-card__error">{{ cardErrors[svc.id] }}</p>
      </div>
    </div>

    <!-- ── 新增 / 編輯 Modal ─────────────────────── -->
    <teleport to="body">
      <div v-if="showModal" class="sas-modal-overlay" @click.self="closeModal">
        <div class="sas-modal" role="dialog" :aria-label="editingId ? '編輯服務' : '新增服務'">
          <div class="sas-modal__header">
            <h2 class="sas-modal__title">{{ editingId ? '編輯服務' : '新增服務' }}</h2>
            <button class="sas-modal__close" @click="closeModal" aria-label="關閉">✕</button>
          </div>

          <form class="sas-form" @submit.prevent="onSubmit">

            <!-- 老師名稱 -->
            <div class="sas-field">
              <label for="f-name">老師名稱 <span class="sas-required">*</span></label>
              <input
                id="f-name"
                v-model.trim="form.name"
                type="text"
                placeholder="例：王小美老師"
                class="sas-input"
                :class="{ 'sas-input--error': formErrors.name }"
              />
              <p v-if="formErrors.name" class="sas-field-error">{{ formErrors.name }}</p>
            </div>

            <!-- 老師信箱（選填）-->
            <div class="sas-field">
              <label for="f-teacher-email">老師信箱（選填）</label>
              <input
                id="f-teacher-email"
                v-model.trim="form.teacher_email"
                type="email"
                placeholder="例：teacher@example.com"
                class="sas-input"
              />
              <p class="sas-field-hint">填寫後，客人完成預約時系統將自動寄通知信給老師。</p>
            </div>

            <!-- 服務項目（多筆）-->
            <div class="sas-field">
              <label>服務項目 <span class="sas-required">*</span></label>
              <p v-if="formErrors.service_items" class="sas-field-error">{{ formErrors.service_items }}</p>
              <div class="sas-items-header">
                <span class="sas-items-col">項目名稱</span>
                <span class="sas-items-col">時長（分鐘）</span>
                <span class="sas-items-col">價格（NT$）</span>
                <span class="sas-items-col sas-items-col--note">備註</span>
                <span class="sas-items-col--action"></span>
              </div>
              <div
                v-for="(item, idx) in form.service_items"
                :key="idx"
                class="sas-items-row"
              >
                <input
                  v-model.trim="item.name"
                  type="text"
                  placeholder="例：護膚"
                  class="sas-input sas-items-col"
                />
                <input
                  v-model.number="item.duration_minutes"
                  type="number"
                  min="1"
                  max="480"
                  placeholder="60"
                  class="sas-input sas-items-col"
                />
                <input
                  v-model.number="item.price"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="sas-input sas-items-col"
                />
                <button
                  type="button"
                  class="sas-note-btn sas-items-col sas-items-col--note"
                  @click="openNotePopup(idx)"
                  :title="item.price_note || '點擊輸入備註'"
                >
                  <span v-if="item.price_note" class="sas-note-btn__val">{{ item.price_note }}</span>
                  <span v-else class="sas-note-btn__empty">＋</span>
                </button>
                <button
                  v-if="form.service_items.length > 1"
                  type="button"
                  class="sas-items-remove"
                  @click="removeItem(idx)"
                >✕</button>
              </div>
              <button type="button" class="sas-btn sas-btn--secondary sas-btn--sm sas-items-add" @click="addItem">
                ＋ 新增服務項目
              </button>
            </div>

            <!-- 服務說明 -->
            <div class="sas-field">
              <label for="f-desc">服務說明（選填）</label>
              <textarea
                id="f-desc"
                v-model.trim="form.description"
                rows="3"
                placeholder="簡短描述此服務內容…"
                class="sas-input sas-input--textarea"
              />
            </div>

            <!-- 排序 -->
            <div class="sas-field">
              <label for="f-sort">排序（數字小的排前面）</label>
              <input
                id="f-sort"
                v-model.number="form.sort_order"
                type="number"
                min="0"
                placeholder="0"
                class="sas-input sas-input--short"
              />
            </div>

            <!-- 編輯時才顯示啟用 / 停用 -->
            <div v-if="editingId" class="sas-field">
              <label>狀態</label>
              <div class="sas-radio-group">
                <label class="sas-radio">
                  <input type="radio" v-model="form.status" value="active" />
                  <span>上架中</span>
                </label>
                <label class="sas-radio">
                  <input type="radio" v-model="form.status" value="inactive" />
                  <span>停用</span>
                </label>
              </div>
            </div>

            <!-- API 錯誤 -->
            <p v-if="submitError" class="sas-api-error">{{ submitError }}</p>

            <!-- 操作按鈕 -->
            <div class="sas-modal__footer">
              <button
                type="button"
                class="sas-btn sas-btn--secondary"
                @click="closeModal"
              >
                取消
              </button>
              <button
                type="submit"
                class="sas-btn sas-btn--primary"
                :disabled="submitting"
              >
                <span v-if="submitting" class="sas-spinner sas-spinner--sm sas-spinner--white" />
                {{ submitting ? '儲存中…' : (editingId ? '儲存變更' : '建立服務') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </teleport>

    <!-- ── 批次建立時段 Modal ─────────────────────── -->
    <teleport to="body">
      <div v-if="showBatch" class="sas-modal-overlay" @click.self="closeBatch">
        <div class="sas-modal" role="dialog">
          <div class="sas-modal__header">
            <h2 class="sas-modal__title">新增時段 — {{ batchTeacher }}</h2>
            <button class="sas-modal__close" @click="closeBatch">✕</button>
          </div>
          <div class="sas-modal__body">
            <p class="sas-modal__desc">設定日期範圍與每日時段，系統自動建立（已存在略過）。</p>

            <div class="sas-field-row">
              <div class="sas-field">
                <label>開始日期 <span class="sas-req">*</span></label>
                <input v-model="batchForm.start_date" type="date" :min="todayTW" class="sas-input"
                  :class="{ 'sas-input--error': batchErrors.start_date }" />
                <p v-if="batchErrors.start_date" class="sas-field-err">{{ batchErrors.start_date }}</p>
              </div>
              <div class="sas-field">
                <label>結束日期 <span class="sas-req">*</span></label>
                <input v-model="batchForm.end_date" type="date" :min="batchForm.start_date || todayTW" class="sas-input"
                  :class="{ 'sas-input--error': batchErrors.end_date || dateRangeInvalid }" />
                <p v-if="batchErrors.end_date" class="sas-field-err">{{ batchErrors.end_date }}</p>
              </div>
            </div>
            <p v-if="dateRangeInvalid" class="sas-batch-warn">⚠ 結束日期不可早於開始日期</p>

            <!-- 已有時段提示 -->
            <div v-if="existingSlotCount > 0" class="sas-exist-hint"
                 :class="{ 'sas-exist-hint--ok': !hasConflictingSlots && generateAllSlots().length > 0 }">
              <p class="sas-exist-hint__title">
                📅 {{ batchForm.start_date }} ～ {{ batchForm.end_date }} 已有 {{ conflictingSlotCount }} 個時段重複
              </p>
              <p v-if="freeRanges.length" class="sas-exist-hint__sub">
                可新增時間：
                <strong v-for="(r, i) in freeRanges" :key="r.label">
                  {{ r.label }}<span v-if="i < freeRanges.length - 1"> 或 </span>
                </strong>
              </p>
              <div v-if="hasConflictingSlots" class="sas-exist-hint__conflict-row">
                <p class="sas-exist-hint__warn">⚠ 目前設定的時段與現有時段重複</p>
                <label class="sas-duplicate-label">
                  <input type="checkbox" v-model="batchForm.allow_duplicate" />
                  <span>🛏️ 確認為 2 張（含）以上床位，允許相同時段重複新增</span>
                </label>
              </div>
              <p v-else-if="generateAllSlots().length === 0" class="sas-exist-hint__warn">
                ⚠ 時間設定有誤，無法產生任何時段
              </p>
              <p v-else class="sas-exist-hint__ok-text">✅ 目前設定不重疊，可直接建立</p>
            </div>

            <div class="sas-field">
              <label>每日時段設定 <span class="sas-req">*</span></label>
              <p v-if="batchErrors.slots" class="sas-field-err">{{ batchErrors.slots }}</p>

              <!-- 多時間段列表 -->
              <div class="sas-range-list">
                <div
                  v-for="(range, idx) in batchForm.timeRanges"
                  :key="idx"
                  class="sas-range-row"
                >
                  <div class="sas-range-inputs">
                    <div class="sas-batch-col">
                      <label>開始時間</label>
                      <div class="sas-time-picker">
                        <select :value="range.start.split(':')[0]" @change="e => setRangeHour(range, 'start', e.target.value)" class="sas-input sas-time-sel">
                          <option v-for="h in hours" :key="h" :value="h">{{ h }}</option>
                        </select>
                        <span class="sas-time-colon">:</span>
                        <select :value="range.start.split(':')[1]" @change="e => setRangeMin(range, 'start', e.target.value)" class="sas-input sas-time-sel">
                          <option v-for="m in minutes" :key="m" :value="m">{{ m }}</option>
                        </select>
                      </div>
                    </div>
                    <span class="sas-range-sep">～</span>
                    <div class="sas-batch-col">
                      <label>結束時間</label>
                      <div class="sas-time-picker">
                        <select :value="range.end.split(':')[0]" @change="e => setRangeHour(range, 'end', e.target.value)" class="sas-input sas-time-sel">
                          <option v-for="h in hours" :key="h" :value="h">{{ h }}</option>
                        </select>
                        <span class="sas-time-colon">:</span>
                        <select :value="range.end.split(':')[1]" @change="e => setRangeMin(range, 'end', e.target.value)" class="sas-input sas-time-sel">
                          <option v-for="m in minutes" :key="m" :value="m">{{ m }}</option>
                        </select>
                      </div>
                      <label class="sas-nextday-label">
                        <input type="checkbox" v-model="range.nextDay" style="margin:0" />
                        隔天（跨日）
                      </label>
                    </div>
                  </div>
                  <button
                    v-if="batchForm.timeRanges.length > 1"
                    class="sas-range-remove"
                    @click="removeTimeRange(idx)"
                    title="移除此時間段"
                  >✕</button>
                </div>
              </div>

              <button class="sas-range-add" @click="addTimeRange">＋ 新增時間段</button>

              <!-- 間隔設定 -->
              <div class="sas-batch-row" style="margin-top:12px">
                <div class="sas-batch-col sas-batch-col--sm">
                  <label>間隔（分鐘）</label>
                  <select v-model.number="batchForm.interval" class="sas-input">
                    <option :value="30">30</option>
                    <option :value="60">60</option>
                    <option :value="90">90</option>
                    <option :value="120">120</option>
                  </select>
                </div>
              </div>

              <!-- 床位數：永遠顯示，選 2+ 自動啟用多床位模式 -->
              <div class="sas-bed-row">
                <label class="sas-bed-label">🛏️ 床位數</label>
                <div class="sas-bed-options">
                  <label v-for="n in [1,2,3,4]" :key="n" class="sas-bed-option" :class="{ 'sas-bed-option--active': batchForm.bed_count === n }">
                    <input type="radio" :value="n" v-model="batchForm.bed_count" @change="batchForm.allow_duplicate = batchForm.bed_count > 1" />
                    {{ n }}{{ n === 4 ? '+' : '' }} 床
                  </label>
                </div>
                <span v-if="batchForm.bed_count > 1" class="sas-bed-hint">允許相同時段重複新增（第 {{ batchForm.bed_count }} 床）</span>
              </div>

              <p v-if="hasInvalidRanges" class="sas-batch-warn">⚠ 有時間段的開始時間晚於或等於結束時間，請修正後再建立</p>
              <p v-if="hasDuplicateRanges" class="sas-batch-warn">⚠ 有重複或交疊的時間段，請移除重複的項目再建立</p>
              <p v-if="!hasInvalidRanges && !hasDuplicateRanges && generateAllSlots().length === 0" class="sas-batch-warn">⚠ 間隔時間大於設定的時間範圍，無法產生任何時段，請縮短間隔或延長時間範圍</p>
              <p class="sas-batch-preview">預覽：{{ batchPreview }}</p>
            </div>

            <p v-if="batchError" class="sas-api-error">{{ batchError }}</p>
            <div v-if="batchResult" class="sas-api-success">
              建立完成，共新增 {{ batchResult.inserted }} 筆時段。
              <span v-if="batchResult.capacityUpdated"> 🛏️ {{ batchResult.capacityUpdated }} 個時段已增加床位（capacity +1）。</span>
              <span v-if="batchResult.skipped"> （已略過 {{ batchResult.skipped }} 個重複時段）</span>
            </div>
          </div>
          <div class="sas-modal__footer">
            <button class="sas-btn sas-btn--secondary" @click="closeBatch">關閉</button>
            <button class="sas-btn sas-btn--primary" :disabled="batchSubmitting || hasDuplicateRanges || hasInvalidRanges || dateRangeInvalid || (hasConflictingSlots && !batchForm.allow_duplicate) || generateAllSlots().length === 0" @click="submitBatch">
              <span v-if="batchSubmitting" class="sas-spinner sas-spinner--sm sas-spinner--white" />
              {{ batchSubmitting ? '建立中…' : '批次建立' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── 備註輸入彈窗 ─────────────────────────── -->
    <teleport to="body">
      <div v-if="notePopup.show" class="sas-note-overlay" @click.self="cancelNote">
        <div class="sas-note-popup">
          <div class="sas-note-popup__header">
            <span class="sas-note-popup__title">輸入備註</span>
            <button class="sas-note-popup__close" @click="cancelNote">✕</button>
          </div>
          <input
            ref="noteInputRef"
            v-model="notePopup.value"
            type="text"
            maxlength="20"
            placeholder="例：起、以上、含材料費"
            class="sas-note-popup__input"
            @keydown.enter="confirmNote"
          />
          <p class="sas-note-popup__hint">最多 20 字</p>
          <div class="sas-note-popup__footer">
            <button type="button" class="sas-btn sas-btn--secondary" @click="cancelNote">取消</button>
            <button type="button" class="sas-btn sas-btn--primary" @click="confirmNote">確認</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const router          = useRouter()
const route           = useRoute()
const storeAdminStore = useStoreAdminStore()
const updateObStep    = inject('updateObStep', () => {})

// 新手引導
const obSvcKey  = computed(() => `jili_ob_s_${storeAdminStore.storeId || ''}`)
const showNextStep = ref(false)

// 相容舊版 query 導向，同時支援漢堡選單直接進入
const onboarding = computed(() => route.query.onboard === '3')
// 真正的「這是當前引導步驟」判斷（不依賴 query param）
const isObActive = computed(() => {
  const id = storeAdminStore.storeId
  if (!id) return false
  if (localStorage.getItem(`jili_ob_skip_${id}`)) return false
  if (!localStorage.getItem(`jili_ob_p_${id}`)) return false
  if (!localStorage.getItem(`jili_ob_h_${id}`)) return false
  return !localStorage.getItem(`jili_ob_s_${id}`)
})
const showObHint = computed(() => (isObActive.value || onboarding.value) && !showNextStep.value)

function goToSlots(svc) {
  router.push({
    name: 'StoreAdminSlots',
    query: {
      teacher:      svc.name,
      service_item: svc.service_item || '',
    },
  })
}

// ── 列表狀態 ────────────────────────────────────────
const services    = ref([])
const loading     = ref(false)
const loadError   = ref('')
const cardErrors    = ref({})  // { [svc.id]: string }
const toggleLoading = ref({})  // { [svc.id]: boolean }
const deleteLoading = ref({})  // { [svc.id]: boolean }

// ── Modal 狀態 ───────────────────────────────────────
const showModal  = ref(false)
const editingId  = ref(null)   // null = 新增，number = 編輯
const submitting = ref(false)
const submitError = ref('')

const emptyItem = () => ({ name: '', duration_minutes: 60, price: 0, price_note: '' })

const emptyForm = () => ({
  name:          '',
  teacher_email: '',
  description:   '',
  sort_order:    0,
  status:        'active',
  service_items: [emptyItem()],
})

const form       = ref(emptyForm())
const formErrors = ref({})

// ── 載入 ────────────────────────────────────────────
async function load() {
  loading.value   = true
  loadError.value = ''
  const result = await storeAdminStore.fetchServices()
  if (result.unauthorized) {
    router.replace({ name: 'StoreAdminLogin' })
    return
  }
  if (result.success) {
    services.value = result.services
  } else {
    loadError.value = result.message || '載入失敗'
  }
  loading.value = false
}

onMounted(load)

// ── 開啟 Modal ──────────────────────────────────────
function openCreate() {
  editingId.value  = null
  form.value       = emptyForm()
  formErrors.value = {}
  submitError.value = ''
  showModal.value  = true
}

function addItem()       { form.value.service_items.push(emptyItem()) }
function removeItem(idx) { if (form.value.service_items.length > 1) form.value.service_items.splice(idx, 1) }

function openEdit(svc) {
  editingId.value  = svc.id
  form.value = {
    name:          svc.name,
    teacher_email: svc.teacher_email || '',
    description:   svc.description || '',
    sort_order:    svc.sort_order ?? 0,
    status:        svc.status,
    service_items: (svc.service_items && svc.service_items.length)
      ? svc.service_items.map(it => ({ name: it.name, duration_minutes: it.duration_minutes, price: it.price, price_note: it.price_note || '' }))
      : [emptyItem()],
  }
  formErrors.value  = {}
  submitError.value = ''
  showModal.value   = true
}

function closeModal() {
  showModal.value = false
}

// ── 表單驗證 ─────────────────────────────────────────
function validate() {
  formErrors.value = {}
  let ok = true
  if (!form.value.name) { formErrors.value.name = '請填寫老師名稱'; ok = false }
  if (form.value.service_items.every(it => !it.name?.trim())) {
    formErrors.value.service_items = '請至少填寫一個服務項目名稱'
    ok = false
  }
  return ok
}

// ── 送出（新增 or 編輯）────────────────────────────
async function onSubmit() {
  submitError.value = ''
  if (!validate()) return

  submitting.value = true
  try {
    const payload = {
      name:          form.value.name,
      teacher_email: form.value.teacher_email || null,
      description:   form.value.description || null,
      sort_order:    form.value.sort_order ?? 0,
      status:        form.value.status,
      service_items: form.value.service_items.filter(it => it.name?.trim()),
    }

    let result
    if (editingId.value) {
      result = await storeAdminStore.updateService(editingId.value, payload)
    } else {
      result = await storeAdminStore.createService(payload)
    }

    if (result.unauthorized) {
      router.replace({ name: 'StoreAdminLogin' })
      return
    }

    if (result.success) {
      const isNew     = !editingId.value
      const savedName = form.value.name   // 記下名稱，closeModal 後 form 會被清空
      await load()
      closeModal()

      if (isNew) {
        // 新增服務 → 自動開啟該老師的「新增時段」modal
        const isFirstService = isObActive.value || onboarding.value || !localStorage.getItem(obSvcKey.value)
        if (isFirstService) {
          localStorage.setItem(obSvcKey.value, '1')
          showNextStep.value = true   // submitBatch 成功後會帶 onboard=4 跳 Slots
          updateObStep()
        }
        openBatchForSvc({ name: savedName })
      }
    } else {
      submitError.value = result.message || '操作失敗，請稍後再試'
    }
  } finally {
    submitting.value = false
  }
}

// ── 刪除服務 ────────────────────────────────────────
async function deleteService(svc) {
  cardErrors.value[svc.id] = ''
  if (svc.status === 'active') {
    cardErrors.value[svc.id] = '請先停用此服務，再進行刪除'
    return
  }
  if (!confirm(`確定要刪除「${svc.name}」嗎？\n\n⚠ 此操作無法復原，刪除後此老師的所有時段資料也會一併清除。`)) return
  deleteLoading.value[svc.id] = true
  const result = await storeAdminStore.deleteService(svc.id)
  if (result.unauthorized) { router.replace({ name: 'StoreAdminLogin' }); return }
  if (result.success) {
    services.value = services.value.filter(s => s.id !== svc.id)
  } else {
    cardErrors.value[svc.id] = result.message || '刪除失敗，請稍後再試'
  }
  deleteLoading.value[svc.id] = false
}

// ── 快速切換啟用 / 停用 ─────────────────────────────
async function toggleStatus(svc) {
  const newStatus = svc.status === 'active' ? 'inactive' : 'active'
  toggleLoading.value[svc.id] = true
  cardErrors.value[svc.id]    = ''

  const result = await storeAdminStore.updateService(svc.id, {
    name:         svc.name,
    description:  svc.description,
    sort_order:   svc.sort_order,
    status:       newStatus,
    service_items: svc.service_items || [],
  })

  if (result.unauthorized) {
    router.replace({ name: 'StoreAdminLogin' })
    return
  }

  if (result.success) {
    svc.status = newStatus   // 直接更新本地，不重撈
  } else {
    cardErrors.value[svc.id] = result.message || '操作失敗'
  }

  toggleLoading.value[svc.id] = false
}

// ── 批次建立時段 ────────────────────────────────────
const todayTW    = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })
const tomorrowTW = new Date(Date.now() + 86400000).toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })


const showBatch       = ref(false)
const batchTeacher    = ref('')
const batchSubmitting = ref(false)
const batchError      = ref('')
const batchResult     = ref(null)
const batchErrors     = ref({})
const batchConflicts  = ref([])

const emptyBatchForm = () => ({
  start_date:      tomorrowTW,
  end_date:        tomorrowTW,
  timeRanges:      [{ start: '09:00', end: '18:00', nextDay: false }],
  interval:        30,
  bed_count:       1,       // 床位數（1=單床，2+=多床）
  allow_duplicate: false,   // 多床位模式：允許相同時段重複新增
})
const batchForm = ref(emptyBatchForm())

// 時間選單選項
const hours   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const minutes = ['00', '10', '20', '30', '40', '50']

function setRangeHour(range, field, h) {
  const m = range[field]?.split(':')[1] || '00'
  range[field] = `${h}:${m}`
}
function setRangeMin(range, field, m) {
  const h = range[field]?.split(':')[0] || '09'
  range[field] = `${h}:${m}`
}

function addTimeRange() {
  batchForm.value.timeRanges.push({ start: '09:00', end: '18:00', nextDay: false })
}
function removeTimeRange(idx) {
  batchForm.value.timeRanges.splice(idx, 1)
}

function minsToTimeStr(mins) {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
}

function generateSlots(dayStart, dayEnd, interval, nextDay = false) {
  if (!dayStart || !dayEnd || !interval) return []
  const [sh, sm] = dayStart.split(':').map(Number)
  const [eh, em] = dayEnd.split(':').map(Number)
  let cur = sh * 60 + sm
  const end = eh * 60 + em + (nextDay ? 24 * 60 : 0)
  if (end <= cur) return []
  const slots = []
  while (cur + interval <= end) {
    const s = minsToTimeStr(cur)
    cur += interval
    slots.push({ start_time: s, end_time: minsToTimeStr(cur) })
  }
  return slots
}

function generateAllSlots() {
  const { timeRanges, interval } = batchForm.value
  return timeRanges.flatMap(r => generateSlots(r.start, r.end, interval, r.nextDay))
}

// 偵測結束日期早於開始日期
const dateRangeInvalid = computed(() => {
  const { start_date, end_date } = batchForm.value
  return !!(start_date && end_date && end_date < start_date)
})

// 偵測開始時間 >= 結束時間（且非隔天）的無效時間段
const hasInvalidRanges = computed(() => {
  const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m }
  return batchForm.value.timeRanges.some(r => !r.nextDay && toMins(r.start) >= toMins(r.end))
})

// 偵測重疊時間段（包含完全相同 or 部分交疊）
const hasDuplicateRanges = computed(() => {
  const toMins = (t, nextDay = false) => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m + (nextDay ? 24 * 60 : 0)
  }
  const ranges = batchForm.value.timeRanges.map(r => ({
    s: toMins(r.start),
    e: toMins(r.end, r.nextDay),
  }))
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const a = ranges[i], b = ranges[j]
      // 兩段有交疊：a 開始 < b 結束 且 b 開始 < a 結束
      if (a.s < b.e && b.s < a.e) return true
    }
  }
  return false
})

const batchPreview = computed(() => {
  const { timeRanges, interval } = batchForm.value
  if (!interval) return '請填寫時間'
  const parts = timeRanges
    .filter(r => r.start && r.end)
    .map(r => {
      const slots = generateSlots(r.start, r.end, interval, r.nextDay)
      const endLabel = r.nextDay ? `隔天 ${r.end}` : r.end
      return slots.length > 0 ? `${r.start}～${endLabel} 共 ${slots.length} 段` : null
    })
    .filter(Boolean)
  if (parts.length === 0) return '時間設定有誤（結束需晚於開始）'
  const total = generateAllSlots().length
  return parts.join('／ ') + `，合計 ${total} 個時段`
})

// 已有時段提示
const existingSlots     = ref([])   // 既有時段原始資料
const existingSlotCount = computed(() => existingSlots.value.length)

// 計算已佔用的時段範圍 & 可用空閒範圍
const existingOccupied = computed(() => {
  if (!existingSlots.value.length) return null
  const times = existingSlots.value.map(s => String(s.start_time).slice(0, 5)).sort()
  const endTimes = existingSlots.value.map(s => String(s.end_time).slice(0, 5)).sort()
  return { from: times[0], to: endTimes[endTimes.length - 1] }
})

const freeRanges = computed(() => {
  if (!existingOccupied.value) return []
  const { from, to } = existingOccupied.value
  const ranges = []
  if (from > '00:00') ranges.push({ label: `00:00 ～ ${from} 以內` })
  if (to < '23:30')   ranges.push({ label: `${to} 之後` })
  return ranges
})

// 產生的時段 start_time 是否跟既有時段衝突，並計算衝突數
// 從既有時段中計算（每天各自算），反映真實重複數量
const conflictingSlotCount = computed(() => {
  if (!existingSlots.value.length) return 0
  const generatedTimes = new Set(generateAllSlots().map(s => s.start_time))
  return existingSlots.value.filter(s => generatedTimes.has(String(s.start_time).slice(0, 5))).length
})
const hasConflictingSlots = computed(() => conflictingSlotCount.value > 0)


async function checkExistingSlots() {
  existingSlots.value = []
  const { start_date, end_date } = batchForm.value
  if (!start_date || !end_date || end_date < start_date) return
  const result = await storeAdminStore.fetchSlotsRangeCheck(start_date, end_date, batchTeacher.value || null)
  if (result?.success && Array.isArray(result.slots)) {
    existingSlots.value = result.slots
  }
}

// 日期變動時重新檢查
watch(() => [batchForm.value.start_date, batchForm.value.end_date], checkExistingSlots)

function openBatchForSvc(svc) {
  batchTeacher.value   = svc.name
  batchForm.value      = emptyBatchForm()
  batchErrors.value    = {}
  batchError.value     = ''
  batchResult.value    = null
  batchConflicts.value = []
  existingSlotCount.value = 0
  showBatch.value      = true
  checkExistingSlots()
}

function closeBatch() { showBatch.value = false }

function validateBatch() {
  const e = {}
  if (!batchForm.value.start_date) e.start_date = '請選擇開始日期'
  if (!batchForm.value.end_date)   e.end_date   = '請選擇結束日期'
  if (batchForm.value.start_date && batchForm.value.end_date &&
      batchForm.value.end_date < batchForm.value.start_date) {
    e.end_date = '結束日期不得早於開始日期'
  }
  if (generateAllSlots().length === 0) {
    e.slots = '時間設定有誤，無法產生任何時段'
  }
  batchErrors.value = e
  return Object.keys(e).length === 0
}


async function submitBatch() {
  batchError.value     = ''
  batchResult.value    = null
  batchConflicts.value = []
  if (!validateBatch()) return

  const timeslots = generateAllSlots()
  const slots = timeslots.map(ts => ({ ...ts, teacher_name: batchTeacher.value || null, service_item: null }))

  batchSubmitting.value = true
  try {
    const result = await storeAdminStore.createSlotsBatch({
      start_date:      batchForm.value.start_date,
      end_date:        batchForm.value.end_date,
      slots,
      allow_duplicate: batchForm.value.bed_count > 1 || batchForm.value.allow_duplicate || false,
    })
    if (result.success) {
      batchResult.value    = result
      batchConflicts.value = result.conflicts || []
      // 批次建立成功 → 跳到該老師的時段管理
      const teacher = batchTeacher.value || null
      const query   = teacher ? { teacher } : {}
      if (showNextStep.value) {
        // 新手引導：帶 onboard=4 做 tab 導覽
        const obSlKey = `jili_ob_sl_${storeAdminStore.storeId || ''}`
        localStorage.setItem(obSlKey, '1')
        showNextStep.value = false
        query.onboard = '4'
      }
      closeBatch()
      router.push({ name: 'StoreAdminSlots', query })
    } else {
      batchError.value = result.message || '建立失敗'
    }
  } catch {
    batchError.value = '網路錯誤，請稍後再試'
  } finally {
    batchSubmitting.value = false
  }
}

// ── 備註彈窗 ───────────────────────────────────────
const notePopup   = ref({ show: false, idx: null, value: '' })
const noteInputRef = ref(null)

function openNotePopup(idx) {
  notePopup.value = { show: true, idx, value: form.value.service_items[idx].price_note || '' }
  nextTick(() => noteInputRef.value?.focus())
}
function confirmNote() {
  if (notePopup.value.idx !== null) {
    form.value.service_items[notePopup.value.idx].price_note = notePopup.value.value.trim()
  }
  notePopup.value.show = false
}
function cancelNote() {
  notePopup.value.show = false
}
</script>

<style scoped>
.sas-page {
  max-width: 800px;
}

/* 新手引導：按鈕脈動圈 */
.sas-header-right { display: flex; align-items: center; gap: 10px; }
.sas-onboard-finger {
  font-size: 0.9rem; font-weight: 700; color: #e67e22;
  animation: sas-finger-right 0.7s ease-in-out infinite;
}
@keyframes sas-finger-right {
  0%, 100% { transform: translateX(0); }
  50%       { transform: translateX(5px); }
}
/* 手機版 onboarding 引導大按鈕 */
.sas-fab-ob {
  display: none;
}
@media (max-width: 768px) {
  .sas-fab-ob {
    display: flex;
    align-items: center;
    gap: 8px;
    position: fixed;
    left: 16px;
    bottom: 28px;
    background: #f97316;
    color: #fff;
    border: none;
    border-radius: 28px;
    padding: 13px 20px;
    font-size: 0.92rem;
    font-weight: 700;
    cursor: pointer;
    z-index: 91;
    box-shadow: 0 4px 18px rgba(249,115,22,0.55);
    animation: sas-ob-float 1.4s ease-in-out infinite;
    white-space: nowrap;
  }
  .sas-fab-ob__icon {
    font-size: 1.1rem;
    font-weight: 900;
    line-height: 1;
  }
  .sas-fab-ob__label {
    letter-spacing: 0.02em;
  }
  @keyframes sas-ob-float {
    0%, 100% { transform: translateY(0);    box-shadow: 0 4px 18px rgba(249,115,22,0.55); }
    50%       { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(249,115,22,0.75); }
  }
}
@keyframes sas-pulse-ring {
  0%   { box-shadow: 0 0 0 0 rgba(230,126,34,0.7); }
  70%  { box-shadow: 0 0 0 10px rgba(230,126,34,0); }
  100% { box-shadow: 0 0 0 0 rgba(230,126,34,0); }
}
.sas-btn--onboard-pulse {
  outline: 3px solid #e67e22;
  outline-offset: 3px;
  animation: sas-pulse-ring 1.2s ease-out infinite;
}
/* 新增時段按鈕 — 服務卡片引導脈動圈 */
.sas-btn--ob-pulse {
  outline: 3px solid #f59e0b;
  outline-offset: 3px;
  animation: sas-pulse-ring 1.2s ease-out infinite;
}
.sas-next-step {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: #f0fdf4; border: 1px solid #86efac;
  border-radius: 8px; padding: 12px 16px; margin-bottom: 16px;
}
.sas-next-step__ok    { color: #16a34a; font-weight: 700; font-size: 0.92rem; }
.sas-next-step__label { font-size: 0.88rem; color: #374151; }
.sas-next-step__btn {
  background: #2563eb; color: #fff; border-radius: 6px;
  padding: 6px 14px; font-size: 0.88rem; font-weight: 600;
  text-decoration: none; transition: background 0.15s; white-space: nowrap;
}
.sas-next-step__btn:hover { background: #1d4ed8; }

/* 頁首 */
.sas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  gap: 12px;
  flex-wrap: wrap;
}
.sas-title { font-size: 1.6rem; color: #2c3e50; margin: 0; }

/* 狀態 */
.sas-state {
  text-align: center;
  padding: 60px 16px;
  color: #aaa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.sas-state--error { color: #c0392b; }
.sas-empty {
  text-align: center;
  padding: 60px 16px;
  color: #aaa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* 服務卡片 */
.sas-list { display: flex; flex-direction: column; gap: 12px; }

.sas-card {
  background: #fff;
  border: 1px solid #e0e6ed;
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  transition: opacity 0.2s;
}
.sas-card--inactive { opacity: 0.6; }

.sas-card__info { flex: 1; min-width: 0; }
.sas-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.sas-card__name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}
.sas-card__desc {
  font-size: 0.88rem;
  color: #7f8c8d;
  margin: 0 0 8px;
  line-height: 1.5;
}
.sas-card__items { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.sas-card__item-tag {
  font-size: 0.8rem; color: #5d4037;
  background: #fdf3ee; border: 1px solid #f0d5c4;
  border-radius: 20px; padding: 2px 10px;
}

/* 服務項目動態列表 */
.sas-items-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.8fr 32px;
  gap: 8px;
  margin-bottom: 4px;
}
.sas-items-header span { font-size: 0.78rem; color: #888; }
.sas-items-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.8fr 32px;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
.sas-items-col { width: 100%; }
.sas-items-header .sas-items-col--note { font-size: 0.78rem; color: #888; }
input.sas-items-col--note { color: #2c3e50; }
.sas-items-remove {
  background: none; border: none; color: #aaa; cursor: pointer;
  font-size: 1rem; padding: 4px; line-height: 1;
  border-radius: 4px;
}
.sas-items-remove:hover { color: #e74c3c; background: #fdedec; }
.sas-items-add { margin-top: 2px; }

.sas-card__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: flex-start;
  flex-wrap: wrap;
}

.sas-card__error {
  width: 100%;
  font-size: 0.82rem;
  color: #c0392b;
  margin: 4px 0 0;
  background: #fdedec;
  border-radius: 6px;
  padding: 6px 10px;
}

/* 標籤 */
.sas-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}
.sas-badge--active   { background: #d4edda; color: #155724; }
.sas-badge--inactive { background: #e9ecef; color: #6c757d; }

/* 按鈕 */
.sas-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 7px;
  border: none;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
}
.sas-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sas-btn--sm { font-size: 0.82rem; padding: 7px 14px; }
.sas-btn--primary    { background: #2c3e50; color: #fff; }
.sas-btn--primary:hover:not(:disabled) { background: #34495e; }
.sas-btn--secondary  { background: transparent; border: 1px solid #bdc3c7; color: #555; }
.sas-btn--secondary:hover:not(:disabled) { background: #f4f6f9; }
.sas-btn--activate   { background: #27ae60; color: #fff; }
.sas-btn--activate:hover:not(:disabled)   { background: #219a52; }
.sas-btn--deactivate { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.sas-btn--deactivate:hover:not(:disabled) { background: #fdedec; }
.sas-btn--slots    { background: #eaf3fb; color: #2471a3; border: 1px solid #aed6f1; }
.sas-btn--slots:hover:not(:disabled)    { background: #d6eaf8; }
.sas-btn--add-slot { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.sas-btn--add-slot:hover:not(:disabled) { background: #d5f5e3; }

/* 批次建立時段 Modal */
.sas-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1200;
}
.sas-modal {
  background: #fff; border-radius: 12px; width: 100%; max-width: 520px;
  max-height: 90vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.sas-modal__header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 24px 14px; border-bottom: 1px solid #ede7e2;
}
.sas-modal__title { font-size: 1.1rem; color: #2c3e50; margin: 0; }
.sas-modal__close {
  background: none; border: none; font-size: 1.1rem;
  color: #aaa; cursor: pointer; padding: 4px 8px; border-radius: 4px;
}
.sas-modal__close:hover { background: #f4f6f9; }
.sas-modal__body  { padding: 20px 24px; }
.sas-modal__desc  { font-size: 0.88rem; color: #888; margin: 0 0 18px; }
.sas-modal__footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 24px; border-top: 1px solid #ede7e2;
}
.sas-field-row { display: flex; gap: 14px; margin-bottom: 16px; }
.sas-field { flex: 1; display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
.sas-field label { font-size: 0.85rem; color: #555; font-weight: 600; }
.sas-req { color: #e74c3c; }
.sas-field-err { font-size: 0.8rem; color: #e74c3c; margin: 0; }
.sas-batch-row { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; }
.sas-batch-col { display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 120px; }
.sas-batch-col label { font-size: 0.82rem; color: #666; }
.sas-batch-col--sm { flex: 0 0 120px; }
.sas-batch-preview { font-size: 0.83rem; color: #8b6f5c; margin: 6px 0 0; }

/* 床位數選擇 */
.sas-bed-row {
  display: flex; align-items: center; gap: 10px;
  flex-wrap: wrap; margin-top: 14px;
}
.sas-bed-label { font-size: 0.88rem; color: #555; white-space: nowrap; font-weight: 500; }
.sas-bed-options { display: flex; gap: 6px; flex-wrap: wrap; }
.sas-bed-option {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 12px; border-radius: 20px; cursor: pointer;
  border: 1px solid #d0c8c0; font-size: 0.85rem; color: #555;
  transition: background 0.15s, border-color 0.15s;
}
.sas-bed-option input[type="radio"] { display: none; }
.sas-bed-option--active {
  background: #8b6f5c; color: #fff; border-color: #8b6f5c; font-weight: 600;
}
.sas-bed-hint {
  font-size: 0.8rem; color: #856404;
  background: #fff3cd; border: 1px solid #ffc107;
  border-radius: 12px; padding: 2px 10px; white-space: nowrap;
}
.sas-batch-warn {
  font-size: 0.83rem;
  color: #b7620a;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 6px 12px;
  margin: 8px 0 0;
}
.sas-batch-info {
  font-size: 0.83rem;
  color: #155724;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0 0;
}
.sas-exist-hint {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 8px 0 0;
}
.sas-exist-hint--ok {
  background: #f0faf4;
  border-color: #a8d5b5;
}
.sas-exist-hint__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #5d4037;
  margin: 0 0 6px;
}
.sas-exist-hint--ok .sas-exist-hint__title { color: #2d6a4f; }
.sas-exist-hint__sub {
  font-size: 0.84rem;
  color: #4a3728;
  margin: 0 0 6px;
}
.sas-exist-hint__warn {
  font-size: 0.82rem;
  color: #b7620a;
  margin: 6px 0 0;
  border-top: 1px solid #ffe082;
  padding-top: 8px;
}
.sas-exist-hint__ok-text {
  font-size: 0.82rem;
  color: #2d6a4f;
  margin: 6px 0 0;
  border-top: 1px solid #a8d5b5;
  padding-top: 8px;
}
.sas-exist-hint__conflict-row {
  margin: 6px 0 0;
  border-top: 1px solid #ffe082;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sas-duplicate-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  font-size: 0.86rem;
  color: #5a3e00;
  background: #fffbe6;
  border: 1px solid #ffd666;
  border-radius: 6px;
  padding: 7px 12px;
  margin-top: 4px;
  user-select: none;
}
.sas-duplicate-label input[type="checkbox"] {
  width: 16px; height: 16px; cursor: pointer;
  accent-color: #e67e22;
}

/* 多時間段 */
.sas-range-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.sas-range-row { display: flex; align-items: flex-end; gap: 8px; background: #f8f9fa; border: 1px solid #e8e8e8; border-radius: 6px; padding: 10px 12px; }
.sas-range-inputs { display: flex; align-items: flex-end; gap: 8px; flex: 1; flex-wrap: wrap; }
.sas-range-sep { font-size: 1rem; color: #999; padding-bottom: 6px; flex-shrink: 0; }
.sas-range-remove { background: none; border: 1px solid #ddd; border-radius: 4px; color: #aaa; font-size: 0.8rem; cursor: pointer; padding: 4px 8px; flex-shrink: 0; transition: all 0.15s; line-height: 1; }
.sas-range-remove:hover { border-color: #e74c3c; color: #e74c3c; background: #fff5f5; }
.sas-range-add { background: none; border: 1px dashed #8b6f5c; color: #8b6f5c; font-size: 0.85rem; padding: 6px 14px; border-radius: 6px; cursor: pointer; transition: all 0.15s; margin-bottom: 4px; }
.sas-range-add:hover { background: #f3ede9; }
.sas-time-picker { display: flex; align-items: center; gap: 4px; }
.sas-time-sel { flex: 1; min-width: 0; padding: 6px 4px; text-align: center; }
.sas-time-colon { font-weight: 700; color: #555; flex-shrink: 0; }
.sas-nextday-label { display: flex; gap: 5px; align-items: center; font-size: 0.82rem; color: #666; cursor: pointer; margin-top: 4px; }
.sas-conflict-box { background: #fff8e6; border: 1px solid #f39c12; border-radius: 6px; padding: 10px 14px; margin-top: 12px; font-size: 0.85rem; color: #7d6608; }
.sas-api-error   { font-size: 0.85rem; color: #e74c3c; margin: 10px 0 0; }
.sas-api-success { font-size: 0.85rem; color: #27ae60; margin: 10px 0 0; }
.sas-booking-link { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #b7e4c7; }
.sas-booking-link__url { color: #1a6b9a; font-size: 0.83rem; text-decoration: none; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sas-booking-link__url:hover { text-decoration: underline; }
.sas-booking-link__copy { background: #2d7a4f; color: #fff; border: none; border-radius: 5px; font-size: 0.8rem; padding: 4px 10px; cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: background 0.15s; }
.sas-booking-link__copy:hover { background: #235f3d; }
.sas-btn--danger { background: transparent; border: 1px solid #c0392b; color: #c0392b; }
.sas-btn--danger:hover:not(:disabled) { background: #fdedec; }

/* Spinner */
.sas-spinner {
  display: inline-block;
  width: 26px; height: 26px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.sas-spinner--sm    { width: 16px; height: 16px; border-width: 2px; }
.sas-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Modal */
.sas-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.sas-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
}
.sas-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.sas-modal__title { font-size: 1.1rem; color: #2c3e50; margin: 0; }
.sas-modal__close {
  background: none;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: #aaa;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
}
.sas-modal__close:hover { background: #f4f6f9; color: #555; }
.sas-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  flex-wrap: wrap;
}

/* 表單 */
.sas-form { padding: 20px 24px 24px; }

.sas-field { margin-bottom: 18px; }
.sas-field label {
  display: block;
  font-size: 0.88rem;
  color: #555;
  font-weight: 500;
  margin-bottom: 6px;
}
.sas-required { color: #e74c3c; }

.sas-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 18px;
}

.sas-input {
  width: 100%;
  padding: 10px 13px;
  border: 1px solid #dce1e7;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;
  -webkit-appearance: none;
}
.sas-input:focus {
  outline: none;
  border-color: #2c3e50;
  box-shadow: 0 0 0 3px rgba(44,62,80,0.07);
}
.sas-input--error    { border-color: #e74c3c; }
.sas-input--textarea { resize: vertical; min-height: 80px; }
.sas-input--short    { max-width: 100px; }

.sas-field-error {
  font-size: 0.8rem;
  color: #e74c3c;
  margin: 4px 0 0;
}

.sas-field-hint {
  font-size: 0.8rem;
  color: #888;
  margin: 4px 0 0;
}

/* 狀態 radio */
.sas-radio-group { display: flex; gap: 20px; flex-wrap: wrap; }
.sas-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #444;
}
.sas-radio input { cursor: pointer; }

/* API 錯誤 */
.sas-api-error {
  background: #fdedec;
  border: 1px solid #f5b7b1;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.88rem;
  color: #c0392b;
  margin-bottom: 0;
  line-height: 1.5;
}

@media (max-width: 520px) {
  .sas-field-row { grid-template-columns: 1fr; }
  .sas-card { flex-direction: column; }
  .sas-card__actions { width: 100%; justify-content: flex-start; }
  .sas-modal__footer { flex-direction: column-reverse; }
  .sas-modal__footer .sas-btn { width: 100%; justify-content: center; }
}

/* ── 手機版浮動新增按鈕 ─────────────────────────── */
.sas-fab {
  display: none;
}
@media (max-width: 768px) {
  .sas-fab {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    right: 20px;
    bottom: 28px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #2c3e50;
    color: #fff;
    font-size: 1.8rem;
    font-weight: 300;
    border: none;
    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
    cursor: pointer;
    z-index: 90;
    transition: background 0.15s, transform 0.15s;
  }
  .sas-fab:hover { background: #34495e; transform: scale(1.08); }
  .sas-fab:active { transform: scale(0.95); }
}

/* ── 備註按鈕（代替原 input）──────────────────── */
.sas-note-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  background: #f8f9fa;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 8px;
  transition: border-color 0.15s, background 0.15s;
}
.sas-note-btn:hover { border-color: #9ca3af; background: #f1f5f9; }
.sas-note-btn__val   { color: #374151; overflow: hidden; text-overflow: ellipsis; }
.sas-note-btn__empty { color: #9ca3af; font-size: 1rem; line-height: 1; }

/* ── 備註彈窗 ──────────────────────────────────── */
.sas-note-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 600;
  padding: 20px;
}
.sas-note-popup {
  background: #fff;
  border-radius: 14px;
  width: 100%; max-width: 340px;
  padding: 20px 20px 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.sas-note-popup__header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
}
.sas-note-popup__title { font-size: 1rem; font-weight: 700; color: #2c3e50; }
.sas-note-popup__close {
  background: none; border: none; font-size: 1.1rem; color: #aaa;
  cursor: pointer; padding: 2px 6px; border-radius: 4px;
}
.sas-note-popup__close:hover { background: #f0f0f0; color: #555; }
.sas-note-popup__input {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 1.05rem;
  border: 1.5px solid #d1d5db; border-radius: 8px;
  outline: none; color: #374151;
}
.sas-note-popup__input:focus { border-color: #2c3e50; }
.sas-note-popup__hint {
  font-size: 0.8rem; color: #aaa; margin: 6px 0 16px; text-align: right;
}
.sas-note-popup__footer {
  display: flex; gap: 10px; justify-content: flex-end;
}
</style>
